import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { comics, getComicById, categoryList, type Chapter } from "@/mocks/comics";

const siteUrl = "https://vhzhn02.cn";

function categoryName(key: string) {
  return categoryList.find((c) => c.key === key)?.name ?? key;
}

export default function Detail() {
  const { id } = useParams();
  const comic = getComicById(id ?? "");
  const [reading, setReading] = useState<Chapter | null>(null);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="mx-auto flex min-h-[60vh] w-full max-w-7xl flex-col items-center justify-center gap-3 px-4">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
          <p className="text-foreground-600">没有找到这部作品</p>
          <Link
            to="/category/all"
            className="rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回漫画库
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const related = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 漫画简介与章节目录`,
    description: comic.description,
    image: comic.cover,
    author: { "@type": "Organization", name: "漫天堂" },
    publisher: { "@type": "Organization", name: "漫天堂", url: siteUrl },
    dateModified: comic.lastUpdate,
    inLanguage: "zh-CN",
  };

  return (
    <>
      <JsonLd data={[schema]} />
      <Navbar />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { name: "漫画库", path: "/category/all" },
            { name: categoryName(comic.category), path: `/category/${comic.category}` },
            { name: comic.title },
          ]}
        />

        {/* Header */}
        <section className="mt-4 flex flex-col gap-6 rounded-lg border border-background-200/70 bg-background-50 p-5 md:flex-row md:p-6">
          <div className="w-40 shrink-0 self-center md:w-56">
            <img
              src={comic.cover}
              alt={`${comic.title} 漫画封面`}
              title={`${comic.title} 漫画封面`}
              className="aspect-[3/4] w-full rounded-lg object-cover object-top"
            />
          </div>
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                  {categoryName(comic.category)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" /> {comic.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-flag-line" /> {comic.status}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-secondary-500" /> {comic.rating} 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" /> {comic.lastUpdate}
                </span>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-foreground-700">{comic.description}</p>

            <div className="flex flex-wrap gap-2">
              {comic.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-auto flex flex-wrap gap-3 pt-2">
              {comic.chapters[0] && (
                <button
                  onClick={() => setReading(comic.chapters[0])}
                  className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-play-circle-fill" />
                  开始阅读
                </button>
              )}
              <Link
                to="/category/all"
                className="flex items-center gap-2 whitespace-nowrap rounded-full bg-background-100 px-6 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-200"
              >
                <i className="ri-grid-line" />
                更多同类漫画
              </Link>
            </div>
          </div>
        </section>

        {/* Chapter list */}
        <section className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              章节目录 <span className="text-sm font-normal text-foreground-500">（{comic.chapters.length} 话）</span>
            </h2>
            <span className="text-sm text-foreground-500">点击章节开始阅读</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {comic.chapters.map((ch) => (
              <button
                key={ch.id}
                onClick={() => setReading(ch)}
                className="group flex items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-50 px-4 py-3 text-left transition-colors hover:border-primary-300"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <i className="ri-book-read-line text-foreground-400 group-hover:text-primary-600" />
                  <span className="truncate text-sm font-medium text-foreground-900">{ch.title}</span>
                </div>
                <span className="shrink-0 text-xs text-foreground-500">{ch.views}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              同类漫画推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Reader modal */}
      {reading && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 px-4"
          onClick={() => setReading(null)}
          role="dialog"
          aria-modal="true"
          aria-label="阅读器"
        >
          <div
            className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-lg bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-background-200/70 px-5 py-4">
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-foreground-950">
                  {comic.title} · {reading.title}
                </h3>
                <p className="text-xs text-foreground-500">
                  更新于 {reading.updateTime} · 阅读量 {reading.views}
                </p>
              </div>
              <button
                onClick={() => setReading(null)}
                className="ml-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200"
                aria-label="关闭阅读器"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-6 text-center">
              <i className="ri-file-text-line text-5xl text-foreground-300" />
              <p className="mt-4 text-base font-medium text-foreground-900">
                正在阅读「{reading.title}」
              </p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
                这里是《{comic.title}》的在线阅读器。漫画正文即将为你加载，请从章节目录中选择并开始阅读，畅享连载更新的乐趣。
              </p>
              <button
                onClick={() => setReading(null)}
                className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                继续浏览目录
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}